/*
 * Helper functions
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import { ActivatedRouteSnapshot } from '@angular/router';
import { Constants } from 'app/shared/config/constants';

const isEnabledFlag = ( value: any ): boolean => value === true || value === 'true';

const hasEnabledQueryParam = ( value: string | null ): boolean => {
  if ( !value ) {
    return false;
  }

  try {
    const parsedUrl = new URL ( value, location.origin );
    return isEnabledFlag ( parsedUrl.searchParams.get ( 'viewInIframe' ) ) ||
      isEnabledFlag ( parsedUrl.searchParams.get ( 'showInIframe' ) );
  } catch ( _error ) {
    return false;
  }
};

export const isHandOffApp = ( app?: string | null ): boolean =>
  !!app && Constants.authRedirectHandOffApps.includes ( app );

export const getRelativeUri = ( path: string ) => {
  const basePath = new URL ( document.getElementsByTagName ( 'base' )[ 0 ].href )?.pathname
    .replace ( /^\/|\/$/g, '' );
  return path.replace ( RegExp ( basePath ), '' ).replace ( /^\/$/, '' );
};

export const getReturnUriQueryParams = ( route: ActivatedRouteSnapshot ): any => {
  if ( route?.queryParams?.[ Constants.authRedirectAppParam ] ) {
    return route.queryParams;
  }

  const uri = getRelativeUri ( location.pathname );
  const queryParams = route?.queryParams?.returnUrl ? { returnUrl: route.queryParams?.returnUrl }
    : { returnUrl: uri + location.search };
  queryParams.returnUrl = queryParams.returnUrl.replace ( '//', '/' );
  if ( queryParams.returnUrl.match ( /login|new-incident|404/g ) || queryParams.returnUrl === '/' ) {
    delete queryParams.returnUrl;
  }
  return queryParams;
};

export const extractRedirectPathFromSearch = ( search: string ): string | null => {
  const normalizedSearch = search.startsWith ( '?' ) ? search.slice ( 1 ) : search;

  if ( !normalizedSearch ) {
    return null;
  }

  const redirectPathParam = 'redirectPath=';
  const redirectPathIndex = normalizedSearch.indexOf ( redirectPathParam );

  if ( redirectPathIndex === -1 ) {
    return null;
  }

  const rawRedirectPath = normalizedSearch.slice ( redirectPathIndex + redirectPathParam.length );
  return decodeURIComponent ( rawRedirectPath.replace ( /\+/g, '%20' ) );
};

const getRedirectPath = ( queryParams?: Record<string, any>, search = location.search ): string | null => {
  return extractRedirectPathFromSearch ( search ) ?? queryParams?.redirectPath ?? null;
};

export const isIframeModeEnabled = ( queryParams?: Record<string, any>, search = location.search ): boolean => {
  return isEnabledFlag ( queryParams?.viewInIframe ) ||
    isEnabledFlag ( queryParams?.showInIframe ) ||
    hasEnabledQueryParam ( getRedirectPath ( queryParams, search ) );
};

export const isShowInIframeModeEnabled = ( queryParams?: Record<string, any>, search = location.search ): boolean => {
  if ( isEnabledFlag ( queryParams?.showInIframe ) ) {
    return true;
  }

  const redirectPath = getRedirectPath ( queryParams, search );

  if ( !redirectPath ) {
    return false;
  }

  try {
    const parsedUrl = new URL ( redirectPath, location.origin );
    return isEnabledFlag ( parsedUrl.searchParams.get ( 'showInIframe' ) );
  } catch ( _error ) {
    return false;
  }
};

export const getUrlSegmentKey = () => {
  const pathArray = location.pathname.split ( '/' );
  if ( pathArray[ pathArray.length - 1 ] === 'trueblue' ) {
    pathArray.push ( 'login' );
  }
  const index = pathArray.indexOf ( 'login' );
  if ( index === -1 ) {
    return null;
  }
  return pathArray.slice ( 1, index ).join ( '/' );
};
